import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { graphql, HeadProps, PageProps } from 'gatsby';
import Layout from '../../component/Layout';
import { Common, ParentPage, SanityArticleCategory, SanityCategoryGroup, SanityImage, SanityNavLink, SanitySEO, SanitySlug, SanityTeaser } from '../../types/common';
import PageContentComponent from '../../component/PageContentComponent';
import Hero from '../../component/Hero';
import ArticlePreview from '../../component/ArticlePreview';
import List from '../../component/List';
import { SEO } from '../../component/SEO';

export const data = graphql`
  query($_id: String) {
    sanityArticleLandingPage(_id: { eq: $_id }) {
      ...ArticleLandingAllFields
    }
    allSanityArticles(sort: {_createdAt: DESC}) {
      ...ArticlesPreviewList
    }
    allSanityArticleCategory(sort: {name: ASC}) {
      nodes {
        _id
        name
      }
    }
    ...Common
  }
`;

type SanityArticle = {
  _id: string;
  _createdAt: string;
  parentPage: {
    slug: {
      current: string;
    }
  },
  slug: {
    current: string;
  }
  title: string;
  image: SanityImage;
  teaser?: SanityTeaser | null;
  highlight?: boolean | null;
  categories?: SanityArticleCategory[] | null;
}



type propsArticleLanding = {
  sanityArticleLandingPage: {
    _id: string;
    parentPage?: ParentPage | null;
    slug: SanitySlug;
    seo: SanitySEO;
    title: string;
    _rawDescription?: any | null;
    navItems: SanityNavLink[];
    categories?: SanityArticleCategory[] | null;
    filter?: {
      advancedFilter?: boolean | null;
      _rawFilterCategories?: SanityArticleCategory[] | null;
      _rawFilterGroups?: SanityCategoryGroup[] | null;
    } | null;
    _rawBottomContent?: any | null;
    teaser: SanityTeaser | null;
    highlightedArticles?: SanityArticle[] | null;
  };
  allSanityArticleCategory: {
    nodes: SanityArticleCategory[];
  };
  allSanityArticles: {
    nodes: SanityArticle[];
  };
} & Common;

const ArticleLandingPage = ({ data }: PageProps<propsArticleLanding>) => {
  const articleLandingData = data.sanityArticleLandingPage;
  const filterData = articleLandingData.filter;

  const highlightedArticles = articleLandingData.highlightedArticles?.map(article => ({ ...article, highlight: true })) || [];

  const allCategories = useMemo(() => data.allSanityArticleCategory.nodes, []);

  const allArticles = useMemo(() => {
    const articles = filterData?.advancedFilter
      ? data.allSanityArticles.nodes
      : data.allSanityArticles.nodes.filter(article => (
        article.categories?.some(category => (
          articleLandingData.categories?.some(pageCategory => pageCategory._id === category._id)
        ))
      ));

    const articlesWithoutHighlighted = articles.filter(article => {
      return !highlightedArticles.some(highlight => highlight._id === article._id)
    })

    return [...highlightedArticles, ...articlesWithoutHighlighted]
  }, []);

  const [articles, setArticles] = useState(allArticles);
  const [filteredCategories, setFilteredCategories] = useState<string[]>([]);
  const [filteredGroups, setFilteredGroups] = useState<string[]>([]);

  const handleChangeFiltered = useCallback((categoryId: string) => {
    setFilteredCategories((prevState) => {
      if (prevState.includes(categoryId)) {
        return prevState.filter(item => item !== categoryId);
      }
      return prevState.concat(categoryId);
    });
  }, []);

  const handleChangeFilteredGroups = useCallback((groupKey: string) => {
    setFilteredGroups((prevState) => {
      if (prevState.includes(groupKey)) {
        return prevState.filter(item => item !== groupKey);
      }
      return prevState.concat(groupKey);
    });
  }, []);

  useEffect(() => {
    if (filteredCategories.length === 0 && filteredGroups.length === 0) {
      setArticles(allArticles);
      return;
    }

    const groupsAux = filterData?._rawFilterGroups?.filter(group => filteredGroups.includes(group._key))

    const filteredArticles = allArticles.filter(article => (
      article.categories?.some(category => (
        filteredCategories.includes(category._id)
        || groupsAux?.some(group => group.categories.some(groupCategory => groupCategory._id === category._id))
      ))
    ));

    setArticles(filteredArticles);
  }, [filteredCategories, filteredGroups]);

  function handleClearFilters() {
    setFilteredCategories([]);
    setFilteredGroups([]);
  }

  return (
    <Layout common={data}>
      <Hero
        title={articleLandingData.title}
        description={articleLandingData._rawDescription}
        breadcrumb={articleLandingData}
        links={articleLandingData.navItems}
      />

      <List
        singularLabel='news'
        pluralLabel='news'
        withoutListPadding={true}
        items={articles.map((article) => (
          <ArticlePreview key={article._id} article={article} />
        ))}
        filter={
          !filterData || (!filterData._rawFilterCategories && !filterData._rawFilterGroups)
            ? undefined
            : (
              !filterData.advancedFilter
                ? {
                  categories: filterData._rawFilterCategories,
                  filteredCategories: filteredCategories,
                  onFilter: handleChangeFiltered,
                  onClearFilters: handleClearFilters,
                }
                : {
                  isAdvanced: true,
                  groups: filterData._rawFilterGroups,
                  categories: allCategories,
                  filteredCategories: filteredCategories,
                  filteredGroups: filteredGroups,
                  onFilter: handleChangeFiltered,
                  onFilterGroup: handleChangeFilteredGroups,
                  onClearFilters: handleClearFilters,
                }
            )
        }
      />

      {articleLandingData._rawBottomContent && (
        <PageContentComponent
          content={articleLandingData._rawBottomContent}
        />
      )}
    </Layout>
  );
};

export default ArticleLandingPage;

export function Head({ data }: HeadProps<propsArticleLanding>) {
  return <SEO data={data.sanityArticleLandingPage} settings={data.sanitySiteSettings} />;
}
